// S2-CC20.6b.2: React hook wrapping GET /v1/llm/calls.
//
// Contract:
//   - filter params (status[], provider[], limit) drive the initial fetch.
//     When they change, we RESET to page 1 (drop prior rows, no cursor).
//   - loadMore() sends `?cursor=<last next_cursor>` plus the same filters
//     and APPENDS new rows to the accumulated list (no replace).
//   - hasMore tracks `next_cursor !== null`; loadMore is a no-op once false
//     or while a request is in flight.
//
// Memoization: we key the effect off JSON.stringify(params). The params
// object is small (two string arrays + a number), the cost is negligible,
// and it gives us structural equality for free without forcing callers
// to memoize their own objects. If this ever shows up in profiling we can
// switch to a hand-rolled key-builder.
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../utils/api';
import type { LlmCallRow, ListLlmCallsResponse } from '../types/llm';

export interface UseLLMCallsParams {
  // Arrays are comma-joined into the query string. Empty arrays and
  // undefined are treated the same: the param is omitted.
  status?: string[];
  provider?: string[];
  // Server clamps to [1, 200] and defaults to 50 when absent. We forward
  // the value as-is (no client-side clamp) so future backend changes are
  // the single source of truth.
  limit?: number;
}

export interface UseLLMCallsResult {
  calls: LlmCallRow[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
}

// Build a `?status=a,b&provider=c&limit=50&cursor=123` query string from the
// hook inputs, omitting empty/undefined values. Exported for testability if
// we ever need it, but not currently referenced outside this file.
function buildQuery(params: UseLLMCallsParams, cursor: number | null): string {
  const qs = new URLSearchParams();
  if (params.status && params.status.length > 0) {
    qs.set('status', params.status.join(','));
  }
  if (params.provider && params.provider.length > 0) {
    qs.set('provider', params.provider.join(','));
  }
  if (params.limit !== undefined) {
    qs.set('limit', String(params.limit));
  }
  if (cursor !== null) {
    qs.set('cursor', String(cursor));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export function useLLMCalls(params: UseLLMCallsParams): UseLLMCallsResult {
  const [calls, setCalls] = useState<LlmCallRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Stable ref to the latest cursor/hasMore/loading state. loadMore uses
  // these via a ref so the callback identity doesn't need to change each
  // render — callers can pass it straight to onClick without re-binding.
  const cursorRef = useRef<number | null>(null);
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);
  cursorRef.current = nextCursor;
  hasMoreRef.current = hasMore;
  loadingRef.current = loading;

  // Stable-key memoization — small object, JSON.stringify is cheap here.
  // See file-level comment for the trade-off.
  const key = JSON.stringify(params);

  // Core fetch runner. `append=false` resets the accumulated rows
  // (filter change or refresh), `append=true` tacks new rows onto the end
  // (loadMore). We update loading synchronously before awaiting fetch so
  // back-to-back loadMore() calls can see the in-flight flag.
  const fetchPage = useCallback(
    async (cursor: number | null, append: boolean) => {
      loadingRef.current = true;
      setLoading(true);
      try {
        const data = await apiFetch<ListLlmCallsResponse>(
          `/v1/llm/calls${buildQuery(params, cursor)}`,
        );
        setCalls(prev => (append ? [...prev, ...data.calls] : data.calls));
        setNextCursor(data.next_cursor);
        setHasMore(data.next_cursor !== null);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load calls');
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    // We depend on the stringified key so `params` changes re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );

  // Initial fetch + filter-change reset. Fires on mount and any time
  // params change. No cursor — always page 1.
  useEffect(() => {
    fetchPage(null, false);
  }, [fetchPage]);

  // Append the next page. Guards against double-click and end-of-list.
  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMoreRef.current) return;
    fetchPage(cursorRef.current, true);
  }, [fetchPage]);

  // Manual refresh — same as filter change: reset to page 1.
  const refresh = useCallback(() => {
    fetchPage(null, false);
  }, [fetchPage]);

  return { calls, loading, error, hasMore, loadMore, refresh };
}
